import React, { useState } from 'react';
import { Button, IconButton, Menu, MenuItem, SvgIcon, Tooltip } from '@mui/material';
import { downloadTextFile, downloadJsonFile, downloadCsvFile } from '../../utils/fileUtils';
import DownloadIcon from '@mui/icons-material/Download';

interface FileDownloaderProps {
  /**
   * 다운로드할 데이터
   */
  data: any;
  
  /**
   * 파일 이름 (확장자 제외)
   */
  filename: string;
  
  /**
   * 버튼 텍스트 (제공되지 않으면 아이콘만 표시)
   */
  buttonText?: string;
  
  /**
   * 아이콘 컴포넌트 (기본값: DownloadIcon)
   */
  icon?: React.ReactNode;
  
  /**
   * 툴팁 텍스트
   */
  tooltip?: string;
  
  /**
   * 버튼 크기
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * 버튼 색상
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  
  /**
   * 버튼 변형
   */
  variant?: 'text' | 'outlined' | 'contained';
}

/**
 * 파일 다운로더 컴포넌트
 * 
 * 데이터를 텍스트, JSON, CSV 등 여러 형식으로 내보낼 수 있는 드롭다운 버튼을 제공합니다.
 */
const FileDownloader: React.FC<FileDownloaderProps> = ({
  data,
  filename,
  buttonText,
  icon = <DownloadIcon />,
  tooltip = '파일 내보내기',
  size = 'medium',
  color = 'primary',
  variant = 'contained'
}) => {
  // 메뉴 앵커 엘리먼트 상태
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // 메뉴 열기 핸들러
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  // 메뉴 닫기 핸들러
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // 텍스트 파일 다운로드 핸들러
  const handleDownloadText = () => {
    let content = '';
    
    if (typeof data === 'string') {
      content = data;
    } else if (typeof data === 'object') {
      content = JSON.stringify(data, null, 2);
    } else {
      content = String(data);
    }
    
    downloadTextFile(content, `${filename}.txt`);
    handleClose();
  };
  
  // JSON 파일 다운로드 핸들러
  const handleDownloadJson = () => {
    downloadJsonFile(data, `${filename}.json`);
    handleClose();
  };
  
  // CSV 파일 다운로드 핸들러
  const handleDownloadCsv = () => {
    // 데이터가 배열인지 확인
    if (Array.isArray(data)) {
      downloadCsvFile(data, `${filename}.csv`);
    } else if (typeof data === 'object' && data !== null) {
      // 객체인 경우 단일 항목 배열로 변환
      downloadCsvFile([data], `${filename}.csv`);
    } else {
      console.error('CSV 다운로드 실패: 유효한 객체 또는 배열이 필요합니다.');
    }
    handleClose();
  };
  
  return (
    <>
      <Tooltip title={tooltip}>
        {buttonText ? (
          <Button
            variant={variant}
            color={color}
            size={size}
            startIcon={icon}
            onClick={handleClick}
          >
            {buttonText}
          </Button>
        ) : (
          <IconButton
            color={color}
            size={size}
            onClick={handleClick}
          >
            {icon}
          </IconButton>
        )}
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleDownloadText}>텍스트 파일 (.txt)</MenuItem>
        <MenuItem onClick={handleDownloadJson}>JSON 파일 (.json)</MenuItem>
        <MenuItem onClick={handleDownloadCsv}>CSV 파일 (.csv)</MenuItem>
      </Menu>
    </>
  );
};

export default FileDownloader; 